/**
 * Spell utility functions
 */
/**
 * Parse whether a spell description indicates a spell attack roll.
 * Open5e descriptions consistently use "ranged spell attack" and "melee spell attack".
 */
export function parseSpellAttackType(desc) {
    const lower = desc.toLowerCase();
    if (lower.includes('ranged spell attack'))
        return 'ranged';
    if (lower.includes('melee spell attack'))
        return 'melee';
    return null;
}
/**
 * Parse whether a spell description requires a saving throw.
 * Open5e descriptions consistently use "{Ability} saving throw" patterns.
 * Returns the required save ability or null if the spell doesn't require a save.
 */
export function parseSpellSaveType(desc) {
    const lower = desc.toLowerCase();
    const abilities = ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'];
    for (const ability of abilities) {
        if (lower.includes(`${ability} saving throw`)) {
            return { ability };
        }
    }
    return null;
}
/**
 * Parse the first damage expression from a spell description.
 * Open5e descriptions consistently use "NdM type damage" patterns.
 * Returns null if no damage is found.
 */
export function parseSpellDamage(desc) {
    const match = desc.match(/(\d+)d(\d+)\s+(\w+)\s+damage/i);
    if (!match)
        return null;
    const dieCount = parseInt(match[1]);
    const dieSize = parseInt(match[2]);
    return {
        dice: `${dieCount}d${dieSize}`,
        damageType: match[3].toLowerCase(),
        dieCount,
        dieSize,
    };
}
/**
 * Parse healing dice from a spell description.
 * Open5e healing spells consistently use "regains hit points equal to NdM + your
 * spellcasting ability modifier" or "regains NdM hit points" patterns.
 */
export function parseSpellHealing(desc) {
    const lower = desc.toLowerCase();
    // Only match if the spell talks about regaining hit points
    if (!lower.includes('regains') && !lower.includes('hit points'))
        return null;
    // Don't match damage spells that happen to mention hit points
    if (lower.includes('damage'))
        return null;
    // Pattern with ability modifier: "NdM + your spellcasting ability modifier"
    const modMatch = desc.match(/(\d+)d(\d+)\s*\+\s*your spellcasting ability modifier/i);
    if (modMatch) {
        const dieCount = parseInt(modMatch[1]);
        const dieSize = parseInt(modMatch[2]);
        return { dice: `${dieCount}d${dieSize}`, dieCount, dieSize, addAbilityMod: true };
    }
    // Pattern without modifier: "regains NdM hit points" or "hit points equal to NdM"
    const plainMatch = desc.match(/(\d+)d(\d+)(?:\s+hit\s+points|\s+hp)/i);
    if (plainMatch) {
        const dieCount = parseInt(plainMatch[1]);
        const dieSize = parseInt(plainMatch[2]);
        return { dice: `${dieCount}d${dieSize}`, dieCount, dieSize, addAbilityMod: false };
    }
    return null;
}
/**
 * Scale cantrip damage based on total character level.
 * Standard 5e cantrip scaling: 1 die at 1st, 2 at 5th, 3 at 11th, 4 at 17th.
 */
export function scaleCantripDamage(baseDamage, characterLevel) {
    let multiplier = 1;
    if (characterLevel >= 17)
        multiplier = 4;
    else if (characterLevel >= 11)
        multiplier = 3;
    else if (characterLevel >= 5)
        multiplier = 2;
    const scaledCount = baseDamage.dieCount * multiplier;
    return {
        ...baseDamage,
        dice: `${scaledCount}d${baseDamage.dieSize}`,
        dieCount: scaledCount,
    };
}
//# sourceMappingURL=spell-utils.js.map